<template>
  <div v-hotkey.stop="keymap" class="smart-player-mediator">
    <SmartPlayerTimeline
      ref="smartPlayerTimeline"
      :available-archive-from="availableArchiveFrom"
      :available-archive-to="currentTime"
      :get-component-with-content="getComponentWithContent"
      :get-line-fragments="getLineFragments"
      :initial-size="timelineSize"
      :time-shift="archiveMode ? timeShift : currentTime"
      class="smart-player-mediator__timeline"
      @time-shift-change="updateCurrentTimeByTimeShiftViaBus"
      @timeline-size-change="timelineSize = $event"
    />

    <div class="smart-player-mediator__controls smart-player-controls">
      <div class="smart-player-controls__main">
        <div class="smart-player-controls__group smart-player-controls__group_start">
          <!-- TIMELINE_SIZES.length - 1 === 8 -->
          <div class="timeline-plus-minus">
            <CamsButton
              :disabled="timelineSize >= 8"
              class="player-button"
              icon-type="only"
              title="Отдалить шкалу архива"
              type="button"
              @click="timelineSize++"
            >
              <svg :class="{'disabled': timelineSize >= 8}" class="player-icon">
                <use xlink:href="./img/icons.svg#icon-circle-minus" />
              </svg>
            </CamsButton>
            <CamsButton
              :disabled="timelineSize <= 0"
              class="player-button"
              icon-type="only"
              title="Приблизить шкалу архива"
              type="button"
              @click="timelineSize--"
            >
              <svg :class="{'disabled': timelineSize <= 0}" class="player-icon">
                <use xlink:href="./img/icons.svg#icon-circle-plus" />
              </svg>
            </CamsButton>
          </div>
          <CamsButton
            class="player-button"
            icon-type="only"
            title="Перейти к текущему моменту"
            type="button"
            @click="showTimeShift()"
          >
            <svg class="player-icon">
              <use xlink:href="./img/icons.svg#icon-focus" />
            </svg>
          </CamsButton>
          <div class="datepicker" title="Календарь архива">
            <CamsButton
              class="player-button"
              icon-type="only"
              size="s"
              type="button"
              @click="$refs.archiveCalendar.open()"
            >
              <svg class="player-icon">
                <use xlink:href="./img/icons.svg#icon-calendar" />
              </svg>
            </CamsButton>
            <SmartInputDate
              ref="archiveCalendar"
              v-model="timeShift"
              :config-flat-pickr="{dateFormat: 'd.m H:i', allowInput: false, minDate: availableArchiveFrom, maxDate: currentTime, static: true}"
              :one-row="true"
              size="s"
              @input="shiftCurrentTimeViaCalendar"
            />
          </div>
          <CamsButton
            :class="{'low-latency-indicator_red': !archiveMode}"
            class="player-button low-latency-indicator"
            icon-type="only"
            title="Перейти к прямой трансляции"
            type="button"
            @click="archiveMode && goToLiveViaBus()"
          >
            <svg :class="archiveMode ? 'player-icon' : 'player-icon_live'"
                 style="height: 14px;width: 37px;">
              <use xlink:href="./img/icons.svg#icon-dot" />
            </svg>
          </CamsButton>
        </div>

        <div class="smart-player-controls__group smart-player-controls__group_center-or-start">
          <CamsButton
            class="player-button"
            icon-type="only"
            title="Отмотать назад на 15 секунд"
            type="button"
            @click="shiftCurrentTimeBySecondsViaBus(-15)"
          >
            <svg class="player-icon">
              <use xlink:href="./img/icons.svg#icon-go-back" />
            </svg>
          </CamsButton>
          <CamsButton
            v-show="paused"
            class="player-button"
            icon-type="only"
            title="Запустить"
            type="button"
            @click="playViaBus()"
          >
            <svg class="player-icon">
              <use xlink:href="./img/icons.svg#icon-play" />
            </svg>
          </CamsButton>
          <CamsButton
            v-show="!paused"
            class="player-button"
            icon-type="only"
            title="Пауза"
            type="button"
            @click="pauseViaBus()"
          >
            <svg class="player-icon">
              <use xlink:href="./img/icons.svg#icon-pause" />
            </svg>
          </CamsButton>
          <CamsButton
            :disabled="!archiveMode"
            class="player-button"
            icon-type="only"
            title="Промотать вперед на 15 секунд"
            type="button"
            @click="shiftCurrentTimeBySecondsViaBus(+15)"
          >
            <svg :class="{'disabled': !archiveMode}" class="player-icon">
              <use xlink:href="./img/icons.svg#icon-go-forward" />
            </svg>
          </CamsButton>
        </div>

        <div class="smart-player-controls__group smart-player-controls__group_end">
          <CamsButton
            v-click-outside="() => {showSpeedMenu = false}"
            class="player-button player-button_with-popup"
            icon-type="only"
            title="Скорость воспроизведения"
          >
            <span
              :class="{'disabled': !archiveMode}"
              class="player-icon player-icon_width-auto player-icon_text-center"
              @click="archiveMode && (showSpeedMenu = !showSpeedMenu)"
            >
              x<span v-text="playbackRate" />
            </span>
            <div v-show="showSpeedMenu" class="smart-player__popup smart-player__speed-select">
              <div
                :class="{'smart-player__popup-option_selected': playbackRate === 0.5}"
                class="smart-player__popup-option"
                @click="setPlaybackRateViaBus(0.5)"
              >
                x0.5
              </div>
              <div
                :class="{'smart-player__popup-option_selected': playbackRate === 1.0}"
                class="smart-player__popup-option"
                @click="setPlaybackRateViaBus(1.0)"
              >
                x1
              </div>
              <div
                :class="{'smart-player__popup-option_selected': playbackRate === 2.0}"
                class="smart-player__popup-option"
                @click="setPlaybackRateViaBus(2.0)"
              >
                x2
              </div>
              <div
                :class="{'smart-player__popup-option_selected': playbackRate === 4.0}"
                class="smart-player__popup-option"
                @click="setPlaybackRateViaBus(4.0)"
              >
                x4
              </div>
              <div
                :class="{'smart-player__popup-option_selected': playbackRate === 8.0}"
                class="smart-player__popup-option"
                @click="setPlaybackRateViaBus(8.0)"
              >
                x8
              </div>
            </div>
          </CamsButton>
          <CamsButton
            :disabled="!fullscreenElementOrSelector"
            class="player-button"
            icon-type="only"
            title="Развернуть на весь экран"
            type="button"
            @click="switchFullscreen()"
          >
            <svg :class="{'disabled': !fullscreenElementOrSelector}" class="player-icon">
              <use xlink:href="./img/icons.svg#icon-fullscreen" />
            </svg>
          </CamsButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SmartPlayerTimeline from "./SmartPlayerTimeline.vue";
import {playerBusMixin} from "./mixins.js";
import {exitFullscreen, requestFullscreenByElement} from "../../lib/helpers.js";

/**
 * Посредник между элементами управления плеером и множеством плееров.
 * Предназначен для использования в мультиэкране.
 * Использует общую шину сообщений для передачи команд в плееры, а так же повторяет часть кода плеера в части работы элементов управления.
 */
export default {
  components: {
    SmartPlayerTimeline,
  },
  mixins: [playerBusMixin],
  props: {
    /**
     * @see SmartPlayerTimeline.props.availableArchiveFrom
     */
    availableArchiveFrom: {
      type: Date,
      required: true,
    },
    /**
     * @see SmartPlayerTimeline.props.getLineFragments
     */
    getLineFragments: {
      type: Function,
      required: true,
    },
    /**
     * Количество мс между запуском функции синхронизации временного сдвига межу плеерами и медиатором.
     */
    syncTimeShift: {
      type: Number,
      default: 5000
    },
    /**
     * Элемент DOM или селектор элемента, который будет отправлен в полноэкранный режим по нажатию кнопки, находящейся в компоненте.
     */
    fullscreenElementOrSelector: {
      type: [String, Element],
      default: null,
    },
    /**
     * Флаг для включения управления плеером через горячие клавиши.
     */
    hotKeysEnabled: {
      type: Boolean,
      default: true,
    },
    /**
     * Функция для работы механизма отображения контента над шкалой. Пробрасывается напрямую в {@see SmartPlayerTimeline} см. детали там.
     */
    getComponentWithContent: {
      type: Function,
      default() {
      },
    },
  },
  data() {
    return {
      archiveMode: false, // Изначально архивный режим выключен.
      timerForDeliveryTimeShift: null, // Таймер для периодической доставки временного сдвига в шину для синхронизации их с плеером.
      timerForCurrentTime: null, // Таймер для вычисления нового времени.
      // Ежесекундное определение нового времени для прямой трансляции.
      // Оно же - мнимый конец архива, в условиях работы на множестве плееров согласимся что последний архив должен быть отображен на шкале.
      currentTime: new Date(),
      timeShift: new Date(), // Абсолютное время воспроизведения (для архива).
      // Настройки кнопок управления из плеера.
      paused: false,
      playbackRate: 1.0,
      showSpeedMenu: false, // Флаг состояния отображения поп-апа для выбора скорости видео.
      timelineSize: 0, // Порядковый номер в размерах временной шкалы.
      keymap: {}, // Настройки горячих клавиш для управления плеером через клавиатуру.
    };
  },
  /**
   * Настройка горячих клавиш.
   */
  created() {
    if (this.hotKeysEnabled) {
      this.keymap = {
        "f": this.switchFullscreen,
        "j": () => {
          this.shiftCurrentTimeBySecondsViaBus(-10);
        },
        "k": () => {
          this.paused ? this.playViaBus() : this.pauseViaBus();
        },
        "l": () => {
          this.shiftCurrentTimeBySecondsViaBus(+10);
        },
        "space": () => {
          this.paused ? this.playViaBus() : this.pauseViaBus();
        },
        "left": () => {
          this.shiftCurrentTimeBySecondsViaBus(-5);
        },
        "right": () => {
          this.shiftCurrentTimeBySecondsViaBus(+5);
        },
      };
    }
  },
  /**
   * Регистрация таймеров.
   */
  mounted() {
    this.timerForCurrentTime = setInterval(() => {
      this.currentTime = new Date();
      if (!this.paused && this.archiveMode) {
        // Изменение сдвига невозможно в режиме паузы.
        this.timeShift = new Date(+this.timeShift + (1000 * this.playbackRate));
        /**
         * Это событие отправляет время воспроизведения по медиатору типа `date` с частотой в 1 сек.
         *
         * @event time-shift-update
         */
        this.$emit("time-shift-update", this.timeShift);
      }
    }, 1000);

    this.timerForDeliveryTimeShift = setInterval(() => {
      this.currentTime = new Date();
      if (!this.paused && this.archiveMode) {
        // Синхронизация сдвига невозможна в режиме паузы.
        this.updateCurrentTimeByTimeShiftViaBus(this.timeShift);
      }
    }, this.syncTimeShift);
  },
  /**
   * Отключение таймеров.
   */
  beforeDestroy() {
    clearInterval(this.timerForCurrentTime);
    clearInterval(this.timerForDeliveryTimeShift);
  },
  methods: {
    /**
     * @see playerBusMixin.methods.play
     */
    play() {
      this.paused = false;
    },
    /**
     * @see playerBusMixin.methods.pause
     */
    pause() {
      this.paused = true;
    },
    /**
     * @see playerBusMixin.methods.setPlaybackRate
     */
    setPlaybackRate(playbackRate) {
      this.playbackRate = playbackRate;
    },
    /**
     * @see playerBusMixin.methods.shiftCurrentTimeBySeconds
     */
    shiftCurrentTimeBySeconds(secondsForShift) {
      const newTimeShift = new Date(this.timeShift);
      newTimeShift.setSeconds(newTimeShift.getSeconds() + secondsForShift);
      this.updateCurrentTimeByTimeShiftViaBus(newTimeShift);
    },
    /**
     * @see playerBusMixin.methods.updateCurrentTimeByTimeShift
     */
    updateCurrentTimeByTimeShift(newTimeShift) {
      if (this.currentTime < newTimeShift) {
        this.goToLiveViaBus();
      } else {
        this.archiveMode = true;
        if (newTimeShift < this.availableArchiveFrom) {
          newTimeShift = new Date(this.availableArchiveFrom);
        }
        this.timeShift = newTimeShift;
        // В архиве нужно синхронизировать состояние флага паузы и воспроизведения после смены сдвига.
        // А так как при смене скачиваемого фрагмента в плеере включается автовоспроизведение, то проще просто сразу всем отправить play.
        this.playViaBus();
      }
    },
    /**
     * @see playerBusMixin.methods.goToLiveViaBus
     */
    goToLive() {
      this.timeShift = new Date(+this.currentTime);
      this.archiveMode = false;
    },
    /**
     * Установит время в плеере в то, что было указано в календаре.
     */
    shiftCurrentTimeViaCalendar(timeShift) {
      this.updateCurrentTimeByTimeShiftViaBus(timeShift);
      this.showTimeShift();
    },
    /**
     * Прямой вызов метода временной шкалы для однократного отображения индикатора текущего прогресса воспроизведения.
     */
    showTimeShift() {
      this.$refs.smartPlayerTimeline && this.$refs.smartPlayerTimeline.setFollowTimeShift();
    },
    /**
     * Включение/выключение полноэкранного режима.
     * После перехода к полноэкранному режиму необходимо корректно отобразить временную шкалу.
     * Если до полноэкранного режима видимая часть шкалы находится близко к ее краю, то после перехода за пределами шкалы может быть
     * показана пустая неотрендереная область, для избежания этого нужен прямой вызов метода временной шкалы для ее ререндера.
     */
    async switchFullscreen() {
      const fullscreenElement = _.isString(this.fullscreenElementOrSelector) ? document.querySelector(this.fullscreenElementOrSelector) : this.fullscreenElementOrSelector;
      if (!fullscreenElement) {
        return;
      }
      document.fullscreenElement === fullscreenElement ? await exitFullscreen() : await requestFullscreenByElement(fullscreenElement);
      this.$refs.smartPlayerTimeline && this.$refs.smartPlayerTimeline.rerenderSeekBar();
    },
  }
};
</script>

<style lang="scss">
@import "./scss/style.scss";

.smart-player-mediator {
  &__timeline {
    background-color: $cams-color-white;
    padding-top: 8px;
    padding-bottom: 8px;
  }

  &__controls {
    padding-top: 5px;
    background-color: $cams-color-white;
  }

  :fullscreen &__controls {
    padding-left: 10px;
    padding-right: 10px;
    padding-bottom: 10px;
  }
}
</style>
